// src/context/AuthContext.jsx
//
// Gates the main /admin-dashboard area. Authenticates against the main
// Firebase project (src/utils/firebase.js) using Firebase Auth
// (email/password) instead of a hardcoded username/password pair.
//
// Note: this is separate from src/context/AdminAuthContext.jsx, which
// gates the "Premiums Config" sidebar section against a *different*
// Firebase project (src/utils/adminFirebase.js). The two are
// intentionally independent - this one controls who can reach the admin
// dashboard at all.
import { createContext, useContext, useState, useEffect } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { auth } from "../utils/firebase";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true); // true until Firebase confirms initial state
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthLoading(false);
    });
    return unsub;
  }, []);

  async function login(email, password) {
    setChecking(true);
    setError("");
    try {
      await signInWithEmailAndPassword(auth, email, password);
      return true;
    } catch (err) {
      console.error("[Auth] login error:", err.code);
      if (
        err.code === "auth/invalid-credential" ||
        err.code === "auth/wrong-password" ||
        err.code === "auth/user-not-found"
      ) {
        setError("Invalid username or password.");
      } else if (err.code === "auth/too-many-requests") {
        setError("Too many attempts. Try again later.");
      } else {
        setError("Login failed. Try again.");
      }
      return false;
    } finally {
      setChecking(false);
    }
  }

  async function logout() {
    await signOut(auth);
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!user,
        user,
        authLoading,
        login,
        logout,
        error,
        setError,
        checking,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
